import { useMutation, useQueryClient } from '@tanstack/react-query'
import { TriangleAlert } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { LogoField } from './LogoField'
import { ThemePicker } from './ThemePicker'
import { ApiException, api } from '../../api/client'
import type { UpdateTenantBody } from '../../api/types.gen'
import {
  DEFAULT_BRAND_COLOR,
  THEMES,
  applyTheme,
  deriveTheme,
  isTenantTheme,
  type ThemeChoice,
  type ThemeName,
} from '../../theme'
import { usePageTitle } from '../../lib/usePageTitle'
import { Banner, Button, Card, ICON_STROKE, cx } from '../../ui'
import { useSession, type SessionTenant } from '../auth/useSession'

/**
 * Identidad del estudio.
 *
 * Existe porque `/settings` llevaba meses en la navegación sin ruta detrás: el
 * enlace estaba, la pantalla no.
 *
 * Lo que se decide aquí no es decoración. Es lo que ve el cliente final cuando
 * abre el enlace que le mandaron por WhatsApp, y es la mitad de lo que el
 * estudio está pagando: «tus entregas, con tu nombre».
 */
export function SettingsPage() {
  usePageTitle('Tu identidad')
  const queryClient = useQueryClient()
  const tenant = useSession((state) => state.tenant)
  const refreshTenant = useSession((state) => state.refreshTenant)

  const branding = tenant?.branding

  // Anotado y no asertado: el literal ensancharía `theme` a `string`, y una
  // aserción taparía el problema en vez de fijar el contrato.
  const saved = useMemo<{ color: string; theme: ThemeChoice }>(
    () => ({
      color: branding?.primary_color ?? DEFAULT_BRAND_COLOR,
      theme: isTenantTheme(branding?.theme) ? branding.theme : 'auto',
    }),
    [branding],
  )

  const [color, setColor] = useState(saved.color)
  const [theme, setTheme] = useState<ThemeChoice>(saved.theme)
  const [failure, setFailure] = useState<string | null>(null)

  const dirty = color !== saved.color || theme !== saved.theme

  /*
   | El informe de contraste es una FUNCIÓN del color y el tema, no estado.
   |
   | Se calcula al renderizar con `deriveTheme`, que es pura y no toca el DOM.
   | Guardarlo en estado desde un efecto provocaba un render en cascada por cada
   | movimiento del selector de color, que es un control que dispara `input` a
   | cada píxel del arrastre.
   |
   | Con `auto` se comprueban LOS DOS temas y se informa del peor. Es lo
   | correcto: con `auto` el tema lo decide el móvil del cliente, así que un
   | color que sólo funciona en claro es un color que falla para la mitad de la
   | gente — y el estudio no tiene forma de saber cuál le tocó a cada uno.
   */
  const report = useMemo(() => {
    const names: ThemeName[] = isTenantTheme(theme) ? [theme] : ['editorial', 'galeria']

    return names
      .map((name) => deriveTheme(color, branding?.accent_color, THEMES[name]).report)
      .reduce((worst, candidate) =>
        // Primero manda que falle; entre dos iguales, el que más hubo que mover.
        worst.accessible !== candidate.accessible
          ? worst.accessible
            ? candidate
            : worst
          : candidate.lightnessShift > worst.lightnessShift
            ? candidate
            : worst,
      )
  }, [color, theme, branding?.accent_color])

  /*
   | El panel se repinta mientras se elige, sin guardar.
   |
   | Un selector que sólo enseña miniaturas obliga a guardar para saber si la
   | elección funciona, y a deshacer si no. Aquí el cambio se ve en la pantalla
   | entera —incluida la barra lateral y este mismo formulario— antes de
   | comprometerse a nada.
   */
  useEffect(() => {
    // Sin recordar: el selector de color dispara `input` a cada píxel del
    // arrastre, y cachear cada tono por el que pasa haría que recargar sin
    // guardar dejara puesto el último que se probó en vez del suyo.
    applyTheme({ ...branding, primary_color: color, theme }, false)
  }, [color, theme, branding])

  /*
   | Al salir sin guardar hay que devolver el tema real.
   |
   | Sin esto, quien juguetea con los colores y navega a Proyectos se lleva la
   | vista previa puesta hasta que recargue, y acaba pensando que se guardó
   | algo que no se guardó.
   */
  useEffect(() => () => void applyTheme(useSession.getState().tenant?.branding), [])

  const save = useMutation({
    mutationFn: () =>
      api.patch<{ data: SessionTenant }, UpdateTenantBody>('/tenant', {
        branding: { primary_color: color, theme },
      }),
    onSuccess: async () => {
      setFailure(null)
      // Relee el tenant para que la vista previa y lo guardado coincidan: si no,
      // `dirty` seguiría en true y el botón de guardar no se apagaría nunca.
      await refreshTenant()
      void queryClient.invalidateQueries({ queryKey: ['tenant'] })
    },
    onError: (error) => {
      setFailure(
        error instanceof ApiException
          ? error.message
          : 'No se pudo guardar. Revisa tu conexión e inténtalo otra vez.',
      )
    },
  })

  return (
    // El hueco de abajo sólo existe cuando hay barra de guardado. Sin él la
    // barra pegajosa tapa la última fila de temas, que es justo la que se está
    // mirando cuando aparece.
    <section className={cx('flex max-w-panel flex-col gap-6', dirty && 'pb-4')}>
      <div>
        <h1 className="text-title font-semibold text-ink">Tu identidad</h1>
        <p className="mt-1 max-w-prose text-body text-ink-muted">
          Así ven tus clientes las galerías que les compartes. Los cambios se
          aplican a todos tus enlaces, incluidos los que ya enviaste.
        </p>
      </div>

      {/* El logo primero: es lo que un estudio viene a poner aquí, y el color
          es el ajuste fino que hace después. */}
      <Card>
        <div className="p-5">
          <LogoField
            branding={branding}
            studioName={tenant?.name ?? 'ChasquiTambo'}
            onChanged={refreshTenant}
          />
        </div>
      </Card>

      <Card>
        <div className="flex flex-col gap-4 p-5">
          <div>
            <h2 className="text-subhead font-semibold text-ink">Tu color</h2>
            <p className="mt-1 max-w-prose text-body text-ink-muted">
              Los botones, los enlaces y la marca de las fotos elegidas salen de
              aquí. El resto de la interfaz se ajusta sola.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-3">
              <span className="sr-only">Color de tu marca</span>
              <input
                type="color"
                value={color}
                onChange={(event) => setColor(event.target.value)}
                className="h-11 w-16 cursor-pointer rounded-control border border-line-strong bg-surface p-1"
              />
            </label>

            <code className="rounded-control bg-surface-sunken px-3 py-2 text-label uppercase text-ink-muted">
              {color}
            </code>

            {color !== saved.color && (
              <Button variant="ghost" size="sm" onClick={() => setColor(saved.color)}>
                Deshacer
              </Button>
            )}
          </div>

          {/*
            El aviso de contraste.

            El motor busca una luminosidad que funcione conservando matiz y
            croma, así que casi cualquier color acaba siendo usable. Cuando no
            lo consigue —un blanco puro sobre un tema blanco— hay que decirlo
            AQUÍ y no dejar que se descubra en la galería de un cliente.
          */}
          {!report.accessible && (
            <Banner tone="warning" title="Este color no se va a leer bien">
              <p>
                Con el tema elegido no hay forma de que los botones tengan
                suficiente contraste. Tus clientes van a tener problemas para
                verlos, sobre todo con el móvil al sol. Prueba con un tono más
                oscuro, o cambia de tema.
              </p>
            </Banner>
          )}

          {report.accessible && report.lightnessShift > 0.05 && (
            <p className="flex items-start gap-2 text-caption text-ink-subtle">
              <TriangleAlert
                size={14}
                strokeWidth={ICON_STROKE}
                className="mt-0.5 shrink-0"
                aria-hidden
              />
              <span>
                Ajustamos un poco el brillo de tu color para que los botones se
                lean sobre este fondo. El tono es el mismo.
              </span>
            </p>
          )}
        </div>
      </Card>

      <Card>
        <div className="flex flex-col gap-4 p-5">
          <div>
            <h2 className="text-subhead font-semibold text-ink">El tema de tus galerías</h2>
            <p className="mt-1 max-w-prose text-body text-ink-muted">
              Sobre fondo oscuro las fotos se aprecian mejor, que es lo que casi
              todos los estudios eligen. Pero si tu marca es luminosa, un tema
              claro va a parecer más tuyo.
            </p>
          </div>

          <ThemePicker value={theme} brandColor={color} onChange={setTheme} />
        </div>
      </Card>

      {failure !== null && (
        <Banner tone="danger" title="No se pudo guardar" live="alert">
          {failure}
        </Banner>
      )}

      {/*
        La barra de guardado se queda pegada abajo mientras haya cambios sin
        guardar. Es la señal de que falta algo por hacer, y evita tener que
        buscar el botón después de bajar por el selector de temas.
      */}
      {dirty && (
        <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-3 rounded-card border border-line bg-surface-raised px-4 py-3 shadow-float">
          <p className="text-body text-ink-muted">Tienes cambios sin guardar.</p>

          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setColor(saved.color)
                setTheme(saved.theme)
              }}
            >
              Descartar
            </Button>
            <Button busy={save.isPending && 'Guardando…'} onClick={() => save.mutate()}>
              Guardar cambios
            </Button>
          </div>
        </div>
      )}
    </section>
  )
}
